import React, { useCallback, useMemo } from 'react';
import { Grid, Typography, Button, Box, useMediaQuery, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import QuickBooking from './QuickBooking';
import useStyles from '../styles/styles';
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import { useSelector } from "react-redux";
import { calcEst, showEst, optionsRequired, FONT_FAMILY } from '../common/sharedFunctions';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import EnergySavingsLeafRoundedIcon from '@mui/icons-material/EnergySavingsLeafRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import bgImage from '../assets/img/hero-tesla-night.png';

const ACCENT = '#EAB308';        // jaune CTA
const GREEN = '#22c55e';         // accent villes / vert premium
const WA_NUMBER = '33669675282';
const WA_LINK = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent('Bonjour Allô Driver, je souhaite réserver / commander.')}`;
const TEL_LINK = 'tel:+33669675282';

const Hero = ({ role }) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const rawLang = (i18n && (i18n.language || (i18n.languages && i18n.languages[0]))) || 'fr';
  const lang = rawLang.toLowerCase().startsWith('fr') ? 'fr' : 'en';

  const localTranslations = useMemo(() => ({
    fr: {
      title_main: 'VTC & Plats à emporter',
      title_cities: 'Toulouse · Montauban · Blagnac',
      desc: "Votre chauffeur privé ou vos plats à emporter livrés en 20-30 min. Prix fixe, sans inscription.",
      cta_primary: 'Réserver / Commander',
      cta_whatsapp: 'Contacter sur WhatsApp',
      badge_eco: 'Flotte 100% hybride / électrique',
      badge_price: 'Prix fixe garanti',
      badge_rating: '4.9/5 — 2 800+ avis',
      quick_book: 'Devis instantané',
      pickup_place: 'Adresse de départ',
      drop_place: 'Destination ou restaurant',
      book_now: 'Voir les prix & Réserver',
      contact_us: 'WhatsApp 06 69 67 52 82',
      quick_book_note: "VTC immédiat ou plats à emporter. Prix fixe, sans inscription.",
      info: 'Info'
    },
    en: {
      title_main: 'VTC & Takeaway',
      title_cities: 'Toulouse · Montauban · Blagnac',
      desc: "Your private driver or takeaway delivered in 20-30 min. Fixed price, no signup.",
      cta_primary: 'Book / Order',
      cta_whatsapp: 'Contact on WhatsApp',
      badge_eco: '100% hybrid / electric fleet',
      badge_price: 'Fixed price',
      badge_rating: '4.9/5 — 2,800+ reviews',
      quick_book: 'Instant quote',
      pickup_place: 'Pickup address',
      drop_place: 'Destination or restaurant',
      book_now: 'See prices & Book',
      contact_us: 'Call +33 6 69 67 52 82',
      quick_book_note: "Immediate VTC or takeaway delivery. Fixed price, no signup.",
      info: 'Info'
    }
  }), []);

  const localT = useCallback((key) => {
    if (localTranslations[lang]?.[key]) return localTranslations[lang][key];
    if (localTranslations.en?.[key]) return localTranslations.en[key];
    try {
      const v = t(key);
      if (v && v !== key) return v;
    } catch (e) { /* ignore */ }
    return key;
  }, [t, lang, localTranslations]);

  const settings = useSelector(state => state.settingsdata.settings);
  const auth = useSelector(state => state.auth);
  const loggedIn = useMemo(() => !!(auth?.profile?.uid), [auth?.profile?.uid]);

  const handleBooking = useCallback((e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (loggedIn) {
      const isDriver = role === 'driver';
      const isAdminNoEst = (role === 'admin' || role === 'fleetadmin') && calcEst && !showEst && !optionsRequired;
      navigate(isDriver || isAdminNoEst ? '/bookings' : '/addbookings');
    } else {
      navigate('/login', { state: { redirectTo: '/addbookings' } });
    }
  }, [loggedIn, role, navigate]);

  const Badge = ({ icon, color, children }) => (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.75,
      px: 1.5, py: 0.6, borderRadius: '999px',
      backgroundColor: `${color}1F`,
      border: `1px solid ${color}55`,
      backdropFilter: 'blur(6px)'
    }}>
      <Box sx={{ color, display: 'flex', '& svg': { fontSize: 16 } }}>{icon}</Box>
      <Typography sx={{ color: '#fff', fontSize: 12, fontWeight: 700, letterSpacing: 0.2 }}>
        {children}
      </Typography>
    </Box>
  );

  return (
    <Box
      className={classes.heroBox}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        boxSizing: 'border-box',
        minHeight: { xs: '100vh', md: '88vh' },
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#000'
      }}
      style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}
    >
      {/* Background image */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute', inset: 0, zIndex: 0,
          backgroundImage: `url(${bgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: { xs: 'center', md: 'center right' },
          opacity: 1,
          filter: 'saturate(1.05) contrast(1.05)'
        }}
      />
      {/* Radial premium gradient */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute', inset: 0, zIndex: 1,
          background: 'linear-gradient(180deg, rgba(0,0,0,0.88) 0%, rgba(5,20,12,0.72) 40%, rgba(0,0,0,0.45) 75%, rgba(0,0,0,0.6) 100%)'
        }}
      />
      {/* Subtle grain (SVG noise) */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute', inset: 0, zIndex: 2,
          opacity: 0.08, mixBlendMode: 'overlay', pointerEvents: 'none',
          backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>")`,
          backgroundSize: '160px 160px'
        }}
      />
      {/* Soft green halo top-left */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute', top: '-20%', left: '-10%', width: 600, height: 600, zIndex: 1,
          background: 'radial-gradient(closest-side, rgba(34,197,94,0.18) 0%, rgba(34,197,94,0) 70%)',
          filter: 'blur(20px)'
        }}
      />

      <Box sx={{ position: 'relative', zIndex: 3, width: '100%', maxWidth: 1280, mx: 'auto', px: { xs: 2.5, sm: 3, md: 6 }, py: { xs: 4, md: 16 }, boxSizing: 'border-box' }}>
      <Grid
        container
        spacing={isMobile ? 3 : 6}
        sx={{
          alignItems: 'center',
          justifyContent: { xs: 'center', md: 'flex-start' }
        }}
      >
        {/* LEFT — Text column */}
        <Grid item xs={12} md={7}>
          <Stack spacing={3} sx={{ width: '100%', alignItems: { xs: 'center', md: 'flex-start' }, textAlign: { xs: 'center', md: 'left' } }}>
            <Box>
              <Typography
                component="h1"
                sx={{
                  fontFamily: FONT_FAMILY,
                  color: '#fff',
                  fontWeight: 800,
                  lineHeight: 1.05,
                  letterSpacing: '-0.02em',
                  fontSize: { xs: 'clamp(36px, 9vw, 56px)', md: 'clamp(48px, 5.5vw, 76px)' }
                }}
              >
                {localT('title_main')}
              </Typography>
              <Typography
                component="p"
                sx={{
                  mt: 1,
                  fontFamily: FONT_FAMILY,
                  color: GREEN,
                  fontWeight: 700,
                  fontSize: { xs: 18, md: 26 },
                  letterSpacing: 0.4
                }}
              >
                {localT('title_cities')}
              </Typography>
            </Box>

            <Typography
              sx={{
                color: 'rgba(229,231,235,0.92)',
                fontSize: { xs: 16, md: 18 },
                lineHeight: 1.55,
                maxWidth: 480,
                fontFamily: '"Inter", sans-serif'
              }}
            >
              {localT('desc')}
            </Typography>

            {/* Trust badges */}
            <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap" sx={{ justifyContent: { xs: 'center', md: 'flex-start' } }}>
              <Badge icon={<EnergySavingsLeafRoundedIcon />} color={GREEN}>
                {localT('badge_eco')}
              </Badge>
              <Badge icon={<VerifiedRoundedIcon />} color={ACCENT}>
                {localT('badge_price')}
              </Badge>
              <Badge icon={<StarRoundedIcon />} color="#ffffff">
                {localT('badge_rating')}
              </Badge>
            </Stack>

            {/* CTAs */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ pt: 1, width: '100%', alignItems: { xs: 'center', sm: 'flex-start' } }}
            >
              <Button
                onClick={handleBooking}
                disableElevation
                sx={{
                  px: 4, py: 1.6, minHeight: 56, borderRadius: '12px',
                  backgroundColor: ACCENT, color: '#000',
                  fontWeight: 800, fontSize: 16, letterSpacing: 0.3,
                  textTransform: 'none',
                  width: { xs: '100%', sm: 'auto' },
                  boxShadow: '0 10px 24px rgba(234,179,8,0.35)',
                  transition: 'transform .15s ease, box-shadow .15s ease',
                  '&:hover': {
                    backgroundColor: ACCENT,
                    transform: 'translateY(-1px) scale(1.02)',
                    boxShadow: '0 14px 30px rgba(234,179,8,0.45)'
                  }
                }}
              >
                {localT('cta_primary')}
              </Button>

              <Button
                component="a"
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<WhatsAppIcon />}
                sx={{
                  px: 3, py: 1.6, minHeight: 56, borderRadius: '12px',
                  border: '1.5px solid rgba(255,255,255,0.25)',
                  color: '#fff', backgroundColor: 'transparent',
                  fontWeight: 700, fontSize: 15, letterSpacing: 0.3,
                  textTransform: 'none',
                  width: { xs: '100%', sm: 'auto' },
                  '&:hover': {
                    borderColor: GREEN,
                    backgroundColor: 'rgba(34,197,94,0.08)'
                  }
                }}
              >
                {localT('cta_whatsapp')}
              </Button>
            </Stack>

            {/* Tel link discrete */}
            <Box>
              <Typography
                component="a"
                href={TEL_LINK}
                sx={{
                  color: 'rgba(255,255,255,0.55)', fontSize: 13,
                  textDecoration: 'none',
                  '&:hover': { color: ACCENT, textDecoration: 'underline' }
                }}
              >
                ☎ +33 6 69 67 52 82
              </Typography>
            </Box>
          </Stack>
        </Grid>

        {/* RIGHT — Quick booking card */}
        <Grid item xs={12} md={5}>
          <Box
            sx={{
              position: 'relative',
              borderRadius: '16px',
              border: '1px solid rgba(255,255,255,0.10)',
              backgroundColor: 'rgba(15,18,16,0.55)',
              backdropFilter: 'blur(14px)',
              WebkitBackdropFilter: 'blur(14px)',
              boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
              p: { xs: 2, sm: 2.5 },
              overflow: 'hidden'
            }}
          >
            <QuickBooking
              localT={localT}
              settings={settings}
              classes={classes}
              loggedIn={loggedIn}
              isMobile={isMobile}
              accentColor={GREEN}
              hideCta
            />
          </Box>
        </Grid>
      </Grid>
      </Box>
    </Box>
  );
};

export default Hero;
